import axios from "axios";

export const IsraeliDateOptions = {
  day: "2-digit",
  month: "2-digit",
  year: "numeric",
  hour: "2-digit",
  minute: "2-digit",
  second: "2-digit",
  hour12: false,
};

export function addTime(originalTime, timeToAdd) {
  if (
    originalTime?.includes(":") &&
    timeToAdd?.includes(":") &&
    timeToAdd?.includes(".")
  ) {
    // Split the original time into components
    const [origHours, origMinutes, origSecondsWithMillis] =
      originalTime?.split(":");
    const [origSeconds, origMillis] = origSecondsWithMillis?.split(".");

    // Split the time to add into components
    const [addHours, addMinutes, addSecondsWithMillis] = timeToAdd?.split(":");
    const [addSeconds, addMillis] = addSecondsWithMillis?.split(".");

    // Convert everything to milliseconds
    const totalOriginalMilliseconds =
      (parseInt(origHours) * 3600 +
        parseInt(origMinutes) * 60 +
        parseInt(origSeconds) +
        (parseInt(origMillis) || 0) / 1000) *
      1000;

    const totalAddMilliseconds =
      (parseInt(addHours) * 3600 +
        parseInt(addMinutes) * 60 +
        parseInt(addSeconds) +
        (parseInt(addMillis) || 0) / 1000) *
      1000;

    // Add the additional time in milliseconds
    const newTotalMilliseconds =
      totalOriginalMilliseconds + totalAddMilliseconds;

    // Convert back to hours, minutes, seconds, and milliseconds
    const newHours = Math.floor(newTotalMilliseconds / 3600000);
    const newMinutes = Math.floor((newTotalMilliseconds % 3600000) / 60000);
    const newSeconds = Math.floor((newTotalMilliseconds % 60000) / 1000);
    const newMillis = Math.floor(newTotalMilliseconds % 1000);

    // Format the new time
    return `${String(newHours).padStart(2, "0")}:${String(newMinutes).padStart(
      2,
      "0"
    )}:${String(newSeconds).padStart(2, "0")}.${String(newMillis).padStart(
      2,
      "0"
    )}`;
  }
}

export function isActiveToContinue(state, props) {
  const { processes, selectedRuns, isFileSelected, fileName } = state;
  const [lastObj, secondLastObj] = processes;

  // Check for Stop severity failures - these should disable the button
  const hasLastObjStopFailures = lastObj?.["stopSeverity"]?.length > 0;
  const hasSecondLastObjStopFailures = secondLastObj?.["stopSeverity"]?.length > 0;

  // If any Stop failures exist, disable button
  if (hasLastObjStopFailures || hasSecondLastObjStopFailures) {
    return true; // true = button disabled
  }

  // At this point, we know there are NO Stop failures (handled above)
  // So we only need to check if there are ANY failures at all (including Critical)
  // But Critical failures should NOT disable the button, so we don't need to check for them
  // The original logic below can proceed normally since Stop failures are already filtered out

  if (!isFileSelected) {
    return true;
  }

  if (lastObj?.["checkType"] !== "D4" && lastObj?.["fileName"] === fileName) {
    if (selectedRuns === "D2_ALL" && lastObj?.["selectedRuns"] === "D2_ALL") {
      if (
        !!lastObj?.["dataList"]?.length &&
        selectedRuns === "D2_ALL" &&
        lastObj?.["selectedRuns"] === "D2_ALL" &&
        !props?.isDisabled
      ) {
        return false; // Button ENABLED (Critical failures are allowed, Stop already handled)
      }
    }

    if (
      lastObj &&
      lastObj?.["completedList"]?.length === lastObj?.["dataList"]?.length &&
      selectedRuns === "D2_D3_ALL" &&
      lastObj?.["selectedRuns"] === "D2_D3_ALL" &&
      secondLastObj?.["selectedRuns"] === "D2_D3_ALL"
    ) {
      if (
        lastObj?.["checkType"] === "D3" &&
        secondLastObj?.["checkType"] === "D2"
      ) {
        return false; // Button ENABLED (Critical failures are allowed, Stop already handled)
      } else {
        return true; // Button DISABLED
      }
    }
  }
  return true;
}

// Export function to check if there are Critical (but not Stop) failures in processes
export function hasCriticalFailures(processes) {
  if (!processes?.length) {
    return false;
  }

  const [lastObj, secondLastObj] = processes;

  // Check each process: if it has Critical failures but NO Stop failures
  const lastObjHasCriticalOnly =
    lastObj?.["criticalSeverity"]?.length > 0 &&
    !(lastObj?.["stopSeverity"]?.length > 0);

  const secondLastObjHasCriticalOnly =
    secondLastObj?.["criticalSeverity"]?.length > 0 &&
    !(secondLastObj?.["stopSeverity"]?.length > 0);

  // Return true if either process has Critical-only failures
  return lastObjHasCriticalOnly || secondLastObjHasCriticalOnly;
}

export function addExecutionTime(startTime, executionTime) {
  // Parse the start time into a Date object
  const startDate = new Date(startTime);

  // Convert execution time to milliseconds
  const [hours, minutes, seconds] = executionTime?.split(":");
  const [sec, ms] = seconds?.split(".");

  const totalMilliseconds =
    parseInt(hours) * 60 * 60 * 1000 +
    parseInt(minutes) * 60 * 1000 +
    parseInt(sec) * 1000 +
    (parseInt(ms) || 0); // Handle milliseconds

  // Add the execution time to the start time
  const endDate = new Date(startDate?.getTime() + totalMilliseconds);

  return endDate;
}

export function checkWiseData(filteredData, batchID) {
  return filteredData?.filter(
    (checkData) => checkData?.["batchID"] === batchID
  );
}

export function calculateProgressState(completedData, totalData) {
  return completedData?.length > 0 && totalData?.length > 0
    ? (completedData?.length / totalData?.length) * 100
    : 0;
}

export function getSelectedCriticalSeverityStatus(selectedChecks) {
  return selectedChecks?.reduce(
    (acc, checkList) => {
      if (checkList?.["severity"] === "Report") {
        acc["selectedReportSeverity"] = [
          ...acc["selectedReportSeverity"],
          checkList,
        ];
      } else if (
        checkList?.["severity"] === "Critical" ||
        checkList?.["severity"] === "Stop"
      ) {
        acc["selectedCriticalStopSeverity"] = [
          ...acc["selectedCriticalStopSeverity"],
          checkList,
        ];
      }
      return acc;
    },
    { selectedReportSeverity: [], selectedCriticalStopSeverity: [] }
  );
}

export function getSeveritiesTypes(checkData) {
  return checkData?.reduce(
    (acc, checkList) => {
      if (checkList?.["severity"] === "Report") {
        acc["reportSeverity"] = [...acc["reportSeverity"], checkList];
      } else if (
        checkList?.["severity"] === "Critical" ||
        checkList?.["severity"] === "Stop"
      ) {
        acc["criticalStopSeverity"] = [
          ...acc["criticalStopSeverity"],
          checkList,
        ];
      }
      return acc;
    },
    { reportSeverity: [], criticalStopSeverity: [] }
  );
}

export function createFinalDataPayload(
  checkData,
  selectedRunsForObj,
  dataList,
  calculateCompleted,
  progress,
  calculateReportSeverity,
  calculateCriticalSeverity,
  calculateStopSeverity,
  isSomeCanceled,
  selectedRuns,
  date,
  state,
  selectedChecks,
  selectedCriticalStopSeverity,
  criticalStopSeverity,
  gushValue,
  processValue
) {
  return {
    batchID: checkData?.["batchID"],
    checkType: checkData?.["checkType"],
    batchName: checkData?.["batchName"],
    dataList: selectedRunsForObj?.["isSomeCanceled"]
      ? selectedRunsForObj?.["dataList"]
      : dataList,
    completedList: selectedRunsForObj?.["isSomeCanceled"]
      ? selectedRunsForObj?.["completedList"]
      : calculateCompleted,
    progressStatus: selectedRunsForObj?.["isSomeCanceled"]
      ? selectedRunsForObj?.["progressStatus"]
      : progress,
    isLastProcessEnd: progress === 100 ? true : false,
    executionTime: checkData?.["executionTime"] || "00:00:00.00",
    reportSeverity: calculateReportSeverity,
    criticalSeverity: calculateCriticalSeverity,
    stopSeverity: calculateStopSeverity,
    isSomeCanceled: isSomeCanceled || selectedRunsForObj?.["isSomeCanceled"],
    selectedRuns: selectedRunsForObj?.["selectedRuns"] ?? selectedRuns,
    localStartTime:
      selectedRunsForObj?.["batchID"] === checkData?.["batchID"]
        ? selectedRunsForObj?.["localStartTime"]
        : date,
    ...((selectedRunsForObj?.["localStartTime"] || date) &&
      checkData?.["executionTime"] && {
        localEndTime: addExecutionTime(
          selectedRunsForObj?.["batchID"] === checkData?.["batchID"]
            ? selectedRunsForObj?.["localStartTime"]
            : date,
          checkData?.["executionTime"]
        ),
      }),
    uploadProgress:
      selectedRunsForObj?.["uploadProgress"] ?? state?.["uploadProgress"],
    isCriticalOrStop: selectedRunsForObj?.["isCriticalOrStop"]
      ? selectedRunsForObj?.["isCriticalOrStop"]
      : selectedChecks?.length
      ? selectedCriticalStopSeverity?.find(
          (severity) =>
            (severity?.["severity"] === "Stop" ||
              severity?.["severity"] === "Critical") &&
            severity?.["key"] === checkData?.["checkNumber"]
        )?.value
      : criticalStopSeverity?.find(
          (severity) =>
            (severity?.["severity"] === "Stop" ||
              severity?.["severity"] === "Critical") &&
            severity?.["checkNo"] === checkData?.["checkNumber"]
        ),
    ...(processValue?.length &&
      gushValue?.length && {
        processValue: processValue,
        gushValue: gushValue,
      }),
    ...(selectedRunsForObj?.["processValue"]?.length &&
      selectedRunsForObj?.["gushValue"]?.length && {
        processValue: selectedRunsForObj["processValue"],
        gushValue: selectedRunsForObj["gushValue"],
      }),
  };
}

export function createSelectedRunsForObj(
  checkData,
  date,
  selectedRuns,
  state,
  isSomeCanceled,
  calculateReportSeverity,
  calculateCriticalSeverity,
  calculateStopSeverity,
  selectedChecks,
  selectedCriticalStopSeverity,
  criticalStopSeverity,
  calculateCompleted,
  progress
) {
  return {
    batchID: checkData?.["batchID"],
    selectedRuns: selectedRuns,
    localStartTime: date,
    uploadProgress: state?.["uploadProgress"],
    isSomeCanceled: isSomeCanceled,
    reportSeverity: calculateReportSeverity,
    criticalSeverity: calculateCriticalSeverity,
    stopSeverity: calculateStopSeverity,
    isCriticalOrStop: selectedChecks?.length
      ? selectedCriticalStopSeverity?.find(
          (severity) =>
            (severity?.["severity"] === "Stop" ||
              severity?.["severity"] === "Critical") &&
            severity?.["key"] === checkData?.["checkNumber"]
        )?.value
      : !!criticalStopSeverity?.find(
          (severity) =>
            (severity?.["severity"] === "Stop" ||
              severity?.["severity"] === "Critical") &&
            severity?.["checkNo"] === checkData?.["checkNumber"]
        )?.severity,
    completedList: calculateCompleted,
    progressStatus: progress,
    isLastProcessEnd: progress === "100" ? true : false,
  };
}

export function calculateExitingSeverityReport(newDataList, reportSeverity) {
  return newDataList?.filter((typeData) => {
    const checkNo = reportSeverity?.find(
      (severity) => severity?.["checkNo"] === typeData?.["checkNumber"]
    )?.checkNo;
    return !!checkNo && typeData?.["result"]?.endsWith(".xlsx");
  });
}

export function existingObjectDataUpdate(
  existingResult,
  newDataList,
  selectedChecks,
  criticalStopSeverity,
  selectedRunsForObj,
  checkData,
  calculateCompleted,
  progress,
  isSomeCanceled,
  selectedCriticalStopSeverity,
  existingCalculateReportSeverity,
  existingCalculateCriticalSeverity,
  existingCalculateStopSeverity
) {
  return Object.assign(existingResult, {
    dataList: selectedRunsForObj?.["isSomeCanceled"]
      ? selectedRunsForObj?.["dataList"]
      : newDataList,
    completedList:
      selectedRunsForObj?.["batchID"] === checkData?.["batchID"] &&
      (isSomeCanceled || selectedRunsForObj?.["isSomeCanceled"]) &&
      selectedRunsForObj?.["completedList"]
        ? selectedRunsForObj?.["completedList"]
        : calculateCompleted,
    progressStatus:
      selectedRunsForObj?.["batchID"] === checkData?.["batchID"] &&
      (isSomeCanceled || selectedRunsForObj?.["isSomeCanceled"])
        ? selectedRunsForObj?.["progressStatus"]
        : progress,
    executionTime: checkData?.["executionTime"]?.length
      ? addTime(existingResult?.["executionTime"], checkData?.["executionTime"])
      : existingResult["executionTime"],
    reportSeverity: existingCalculateReportSeverity,
    criticalSeverity: existingCalculateCriticalSeverity,
    stopSeverity: existingCalculateStopSeverity,
    isSomeCanceled:
      isSomeCanceled ||
      existingResult?.["isSomeCanceled"] ||
      selectedRunsForObj?.["isSomeCanceled"],
    isCriticalOrStop:
      selectedRunsForObj?.["isCriticalOrStop"] ?? selectedChecks?.length
        ? selectedCriticalStopSeverity?.find(
            (severity) =>
              ["Stop", "Critical"].includes(severity?.["severity"]) &&
              severity?.["key"] === checkData?.["checkNumber"]
          )?.value
        : criticalStopSeverity?.find(
            (severity) =>
              ["Stop", "Critical"].includes(severity?.["severity"]) &&
              severity?.["checkNo"] === checkData?.["checkNumber"]
          ),
  });
}

export const mergeDWGfiles = async (
  generalEndPoint,
  files,
  browserSessionID,
  batchID,
  batchName,
  DWGFile,
  ExcelFileIds,
  projectId
) => {
  if (DWGFile) {
    const sessionId = browserSessionID?.replace(/['"]+/g, ""); // Remove any double quotes from the session ID

    const userDataString = sessionStorage.getItem("userData");
    let displayName = "";

    if (userDataString) {
      const userData = JSON.parse(userDataString);
      displayName = userData.user.displayName;
    }

    const data = {
      DWGFileId: [...files] || [],
      SessionID: sessionId,
      batchID,
      batchName,
      DWGFile,
      Username: displayName,
      projectId, // pass project id here
      MergeRequest: {
        FileId: [...ExcelFileIds] || [],
        FilePaths: [],
      },
    };
    try {
      const response = await axios.post(
        generalEndPoint + "/mergeDWGfiles",
        data
      );
      return response.status;
    } catch (error) {
      console.error("Error merging files:", error);
      //return response.status;
    }
  }
};
export const resultsFilesDownloadClick = async (
  generalEndPoint,
  batchID,
  fileName,
  fileType
) => {
  axios({
    url:
      generalEndPoint +
      `/downloadMergedFile?batchID=${batchID}&fileType=${fileType}`,
    method: "GET",
    responseType: "blob", // Important
  })
    .then((response) => {
      // Create a URL and trigger download
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement("a");
      link.href = url;
      link.setAttribute("download", fileName);
      document.body.appendChild(link);
      link.click();

      // Clean up
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);
    })
    .catch((error) => {
      console.error("Error downloading the file", error);
    });
};

export const getDWGMerge = (generalEndPoint, batchID) => {
  axios({
    url: generalEndPoint + `/downloadMergedDWG?batchID=${batchID}`,
    method: "GET",
    responseType: "blob", // Important
  })
    .then((response) => {
      return response.data;
    })
    .catch((error) => {
      console.error("Error downloading the file", error);
    });
};

export function filesDownloadClick(generalEndPoint, files, fileName) {
  const data = {
    FileId: [...files] || [],
  };

  axios({
    url: generalEndPoint + `/mergeFiles`,
    method: "POST",
    data: data,
    responseType: "blob", // Important
  })
    .then((response) => {
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement("a");
      link.href = url;
      link.setAttribute("download", fileName);
      document.body.appendChild(link);
      link.click();
      // Clean up
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);
    })
    .catch((error) => {
      console.error("Error downloading the file", error);
    });
}

export function handleDownloadClick(generalEndPoint, fileID, fileName) {
  axios({
    url: generalEndPoint + `/getfile/${fileID}`,
    method: "GET",
    responseType: "blob", // Important
  })
    .then((response) => {
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement("a");
      link.href = url;
      const contentDisposition = response.headers["content-disposition"];
      if (
        contentDisposition &&
        contentDisposition.indexOf("attachment") !== -1
      ) {
        const matches = /filename="([^"]*)"/.exec(contentDisposition);
        if (matches != null && matches[1]) {
          fileName = matches[1];
        }
      }
      link.setAttribute("download", fileName);
      document.body.appendChild(link);
      link.click();
      // Clean up
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);
    })
    .catch((error) => {
      console.error("Error downloading the file", error);
    });
}

export const parseDate = (str) => {
  if (!str) return new Date(NaN);
  const [datePart, timePart] = str.split(" ");
  const [day, month, year] = datePart.split("/").map(Number);
  const [hours = 0, minutes = 0] = timePart
    ? timePart.split(":").map(Number)
    : [];

  return new Date(year, month - 1, day, hours, minutes);
};

export const sortFields = (field, order, data) => {
  if (!Array.isArray(data)) return [];

  let sortedData = [...data];
  if (field === "endTime" || field === "startTime" || field === "updatedDate") {
    sortedData.sort((a, b) => {
      const dateA = parseDate(a[field]);
      const dateB = parseDate(b[field]);

      if (isNaN(dateA) && isNaN(dateB)) return 0;
      if (isNaN(dateA)) return 1;
      if (isNaN(dateB)) return -1;

      return (dateA - dateB) * order;
    });
  } else {
    // For other fields, do alphabetical sort
    sortedData.sort((a, b) => {
      const valA = a[field] ? a[field].toString().toLowerCase() : "";
      const valB = b[field] ? b[field].toString().toLowerCase() : "";
      const comparison = valA.localeCompare(valB);
      return comparison * order;
    });
  }
  return sortedData;
};

export const getCalculatedNoErrors = (dataList) => {
  return dataList?.filter(
    (typeData) =>
      typeData?.["status"] === "Processed" && 
      typeData?.["result"] === "No Errors"
  );
}
export const getCalculatedSeverities = (dataList, checksList, type) => {
  if (type === "Report") {
    return dataList?.filter((typeData) => {
      const checkNo = checksList?.find(
        (severity) =>
          severity?.["checkNo"] === typeData?.["checkNumber"] &&
          severity?.["checkType"] === typeData?.["checkType"] &&
          severity?.["severity"] === "Report"
      )?.["checkNo"];

      return !!checkNo && typeData?.["result"]?.endsWith(".xlsx");
    });
  } else if (type === "Critical") {
    // Only Critical (not Stop)
    return dataList?.filter((typeData) => {
      const check = checksList?.find(
        (severity) =>
          severity?.["checkNo"] === typeData?.["checkNumber"] &&
          severity?.["checkType"] === typeData?.["checkType"]
      );
      return (
        check?.["severity"] === "Critical" &&
        typeData?.["result"]?.endsWith(".xlsx")
      );
    });
  } else if (type === "Stop") {
    // Only Stop
    return dataList?.filter((typeData) => {
      const check = checksList?.find(
        (severity) =>
          severity?.["checkNo"] === typeData?.["checkNumber"] &&
          severity?.["checkType"] === typeData?.["checkType"]
      );
      return (
        check?.["severity"] === "Stop" &&
        typeData?.["result"]?.endsWith(".xlsx")
      );
    });
  }
};

export const getCalculatedCompleted = (dataList, status, batchID) => {
  return dataList?.filter(
    (typeData) =>
      typeData?.["status"] === status && typeData?.["batchID"] === batchID
  );
};

export const getCalculatedRandomErrors = (dataList, batchID) => {
  return dataList?.filter(
    (typeData) =>
      typeData?.["status"] === "Processed" &&
      typeData?.["result"] !== "No Errors" &&
      !typeData?.["result"]?.endsWith(".xlsx") &&
      !typeData?.["result"]?.endsWith(".dwg") &&
      typeData?.["result"] !== "Rebuild Failure" &&
      typeData?.["batchID"] === batchID
  );
};

export function lowercaseFirstLetter(str) {
  if (!str) return str;
  return str.charAt(0).toLowerCase() + str.slice(1);
}

export const timeToSeconds = (time) => {
  const [hours, minutes, seconds] = time?.split(":");
  const [sec, ms] = seconds?.split(".");

  const totalMilliseconds =
    parseInt(hours) * 60 * 60 * 1000 +
    parseInt(minutes) * 60 * 1000 +
    parseInt(sec) * 1000 +
    (parseInt(ms) || 0);
  return totalMilliseconds;
};

export const secondsToTime = (totalSeconds) => {
  const hours = Math.floor(totalSeconds / 3600);
  totalSeconds %= 3600;
  const minutes = Math.floor(totalSeconds / 60);
  const seconds = (totalSeconds % 60).toFixed(2);
  return `${String(hours).padStart(2, "0")}:${String(minutes).padStart(
    2,
    "0"
  )}:${String(seconds).padStart(5, "0")}`;
};

export const getEndTime = (startTime, executionData) => {
  const executionTimeArr = executionData?.map(
    (list) => list?.["executionTime"]
  );

  // Calculate the sum of times
  const totalSeconds = executionTimeArr.reduce(
    (sum, time) => sum + timeToSeconds(time),
    0
  );
  // const totalTime = secondsToTime(totalSeconds);
  const startSecTime = new Date(startTime).getTime();

  const endTime = new Date(startSecTime + totalSeconds);

  return endTime.toLocaleString("he-IL", IsraeliDateOptions);
};

export const generateImageString = () => {
  const now = new Date();

  const year = now.getFullYear();
  const month = String(now.getMonth() + 1).padStart(2, "0"); // Months are 0-based
  const day = String(now.getDate()).padStart(2, "0");
  const hours = String(now.getHours()).padStart(2, "0");
  const minutes = String(now.getMinutes()).padStart(2, "0");
  const seconds = String(now.getSeconds()).padStart(2, "0");

  return `${year}${month}${day}_${hours}${minutes}${seconds}`;
};

export const getProcessName = (processValue, gushValue) => {
  return ` תמ"ר  ${processValue
    ?.replace(/\//g, "\\")
    ?.split("\\")
    .join("/")}, מספר גוש ${gushValue} `;
};

export const capitalizeKeys = (obj) => {
  const newObj = {};
  for (let key in obj) {
    if (obj.hasOwnProperty(key)) {
      const capitalizedKey = key.charAt(0).toUpperCase() + key.slice(1);
      newObj[capitalizedKey] = obj[key];
    }
  }
  return newObj;
};
